import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { coursesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState';
import Pagination from '../components/ui/Pagination';
import { canAssignAnyCourse, canAssignType } from '../utils/coursePermissions';
import { useToast } from '../context/ToastContext';
import ConfirmModal from '../components/ui/ConfirmModal';

const PAGE_SIZE = 12;

// SVG icons for course types
const ShieldIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
    />
  </svg>
);

const AlertTriangleIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const ArrowPathIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182"
    />
  </svg>
);

const UserHardHatIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
    />
  </svg>
);

const BuildingIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
    />
  </svg>
);

const BookOpenIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
    />
  </svg>
);

const CubeIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
    />
  </svg>
);

const ChartBarIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
    />
  </svg>
);

const PencilSquareIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
    />
  </svg>
);

const TrashIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
  </svg>
);

const CheckCircleIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const PlusIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

// Course type configuration — new types from dev
const typeConfig = {
  IIG_SSM: { labelKey: 'courses.typeShort.IIG_SSM', badgeColor: 'blue', icon: ShieldIcon },
  ILM_SSM: { labelKey: 'courses.typeShort.ILM_SSM', badgeColor: 'blue', icon: BuildingIcon },
  IP_SSM: { labelKey: 'courses.typeShort.IP_SSM', badgeColor: 'green', icon: ArrowPathIcon },
  SUPLIMENTARE_SSM: {
    labelKey: 'courses.typeShort.SUPLIMENTARE_SSM',
    badgeColor: 'yellow',
    icon: BookOpenIcon,
  },
  IIG_SU: { labelKey: 'courses.typeShort.IIG_SU', badgeColor: 'red', icon: AlertTriangleIcon },
  ILM_SU: { labelKey: 'courses.typeShort.ILM_SU', badgeColor: 'red', icon: UserHardHatIcon },
  IP_SU: { labelKey: 'courses.typeShort.IP_SU', badgeColor: 'red', icon: ArrowPathIcon },
};

const categories = [
  { value: 'all', labelKey: 'courses.categories.all', icon: BookOpenIcon },
  { value: 'IIG_SSM', labelKey: 'courses.typeShort.IIG_SSM', icon: ShieldIcon },
  { value: 'ILM_SSM', labelKey: 'courses.typeShort.ILM_SSM', icon: BuildingIcon },
  { value: 'IP_SSM', labelKey: 'courses.typeShort.IP_SSM', icon: ArrowPathIcon },
  { value: 'SUPLIMENTARE_SSM', labelKey: 'courses.categories.suplimentare', icon: BookOpenIcon },
  { value: 'IIG_SU', labelKey: 'courses.typeShort.IIG_SU', icon: AlertTriangleIcon },
  { value: 'ILM_SU', labelKey: 'courses.typeShort.ILM_SU', icon: UserHardHatIcon },
  { value: 'IP_SU', labelKey: 'courses.typeShort.IP_SU', icon: ArrowPathIcon },
];

const Courses = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'templates' ? 'templates' : 'courses';
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'templates') next.set('tab', 'templates');
    else next.delete('tab');
    setSearchParams(next);
  };

  const [courses, setCourses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Cine poate vedea/folosi template-uri = responsabili SSM/PSI
  const canManageTemplates =
    hasRole(['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR']) ||
    !!user?.isSSMInstructor ||
    !!user?.isCadruTehnicPSI;

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType]);

  // Cine poate crea/edita cursuri:
  // - SSM admin/instructor (gestionare completă)
  // - Oricine poate atribui măcar un tip (CLM, COMPANY_ADMIN cu bife, etc.)
  const canManageCourses =
    hasRole(['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR']) ||
    canAssignAnyCourse(user, !!user?.managesDepartment);

  // Fetch ambele liste COMPLETE la mount — filtrarea pe tip se face client-side ca să
  // păstrăm counts corecte pe toate categoriile simultan (altfel „IIG_SSM" ar pune 0
  // pe toate celelalte când utilizatorul comută filtrul).
  useEffect(() => {
    fetchCourses();
    if (canManageTemplates) fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await coursesAPI.getAll();
      setCourses(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await coursesAPI.listTemplates();
      setTemplates(response.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        showToast(t('courses.noTemplatePermission'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = (id) => setDeleteId(id);
  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await coursesAPI.delete(deleteId);
      setDeleteId(null);
      // Refetch ambele liste — item-ul șters putea fi fie curs, fie template
      fetchCourses();
      if (canManageTemplates) fetchTemplates();
    } catch (err) {
      showToast(err.response?.data?.error || t('courses.deleteError'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const fullList = activeTab === 'templates' ? templates : courses;
  // `fullList` conține totul (fără filtru de tip) — pentru counts pe categorii.
  // `displayedList` e filtrat pe categorie — pentru paginator și empty state.
  const displayedList =
    selectedType === 'all' ? fullList : fullList.filter((c) => c.type === selectedType);

  const countByType = (type) => {
    if (type === 'all') return fullList.length;
    return fullList.filter((c) => c.type === type).length;
  };

  const totalPages = Math.max(1, Math.ceil(displayedList.length / PAGE_SIZE));
  const pagedCourses = displayedList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('courses.title')}
        subtitle={t('courses.subtitle')}
        actions={
          canManageCourses && (
            <Link to={activeTab === 'templates' ? '/courses/new?template=1' : '/courses/new'}>
              <Button variant="primary" size="md">
                <PlusIcon className="w-4 h-4" />
                {activeTab === 'templates' ? t('courses.newTemplate') : t('courses.newCourse')}
              </Button>
            </Link>
          )
        }
      />

      {/* Tab switcher: Cursuri / Biblioteca template-uri */}
      {canManageTemplates && (
        <Card padding={false}>
          <div className="flex border-b border-slate-200 dark:border-slate-800">
            <button
              onClick={() => setActiveTab('courses')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 font-medium text-sm transition-colors ${
                activeTab === 'courses'
                  ? 'text-primary-600 dark:text-primary-300 border-b-2 border-primary-600'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
              }`}
            >
              <BookOpenIcon className="w-4 h-4" />
              {t('courses.myCoursesTab', { count: courses.length })}
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 font-medium text-sm transition-colors ${
                activeTab === 'templates'
                  ? 'text-primary-600 dark:text-primary-300 border-b-2 border-primary-600'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
              }`}
            >
              📋 {t('courses.templatesLibrary', { count: templates.length })}
            </button>
          </div>
        </Card>
      )}

      {/* Filter tabs */}
      <Card padding={false} className="p-4">
        <div className="flex gap-2 overflow-x-auto pb-1 flex-wrap">
          {categories.map((cat) => {
            const count = countByType(cat.value);
            const isActive = selectedType === cat.value;
            const IconComponent = cat.icon;
            return (
              <button
                key={cat.value}
                onClick={() => setSelectedType(cat.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium whitespace-nowrap transition-colors text-sm ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                <span>{t(cat.labelKey)}</span>
                {cat.value !== 'all' && (
                  <span
                    className={`ml-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                      isActive
                        ? 'bg-primary-500 text-white'
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Course grid */}
      {loading ? (
        <Spinner size="lg" label={t('courses.loadingSpinner')} />
      ) : displayedList.length === 0 ? (
        // Niciun curs deloc → buton „Instruire nouă" pe centru (CTA principal).
        // Gol doar pe categoria curentă → acțiune „Vezi toate".
        fullList.length === 0 ? (
          <EmptyState
            icon={<BookOpenIcon className="w-6 h-6" />}
            title={t('courses.emptyInCategory')}
            description={
              canManageCourses ? t('courses.emptyCreateFirst') : t('courses.emptyWillAppear')
            }
            actionLabel={
              canManageCourses
                ? activeTab === 'templates'
                  ? t('courses.newTemplate')
                  : t('courses.newCourse')
                : undefined
            }
            onAction={
              canManageCourses
                ? () =>
                    navigate(activeTab === 'templates' ? '/courses/new?template=1' : '/courses/new')
                : undefined
            }
          />
        ) : (
          <EmptyState
            icon={<BookOpenIcon className="w-6 h-6" />}
            title={t('courses.emptyInCategory')}
            description={
              canManageCourses ? t('courses.emptyCreateFirst') : t('courses.emptyWillAppear')
            }
            actionLabel={
              canManageCourses && selectedType !== 'all' ? t('courses.viewAll') : undefined
            }
            onAction={
              canManageCourses && selectedType !== 'all' ? () => setSelectedType('all') : undefined
            }
          />
        )
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pagedCourses.map((course) => {
              const config = typeConfig[course.type] || {
                labelKey: null,
                badgeColor: 'gray',
                icon: BookOpenIcon,
              };
              const TypeIcon = config.icon;
              const typeLabel = config.labelKey ? t(config.labelKey) : course.type;

              return (
                <Card
                  key={course.id}
                  hover
                  padding={false}
                  className="flex flex-col overflow-hidden"
                >
                  {/* Card header with badge */}
                  <div className="p-5 pb-0">
                    <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
                      <Badge color={config.badgeColor}>
                        <span className="flex items-center gap-1">
                          <TypeIcon className="w-3.5 h-3.5" />
                          {typeLabel}
                        </span>
                      </Badge>
                      {/* Mini-badge pentru categoria de serviciu (SSM / SU) — claritate vizuală instantă */}
                      {course.type?.endsWith('_SSM') && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 dark:bg-slate-800/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-700">
                          SSM
                        </span>
                      )}
                      {course.type?.endsWith('_SU') && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-50 dark:bg-slate-800/60 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-slate-700">
                          SU
                        </span>
                      )}
                      {course.isGlobal && activeTab === 'templates' && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                          title={t('courses.globalComplianceWarning')}
                        >
                          {t('courses.checkCompliance')}
                        </span>
                      )}
                      {course.isGlobal && activeTab !== 'templates' && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                          title={t('courses.platformCourseTitle')}
                        >
                          📋 Template
                        </span>
                      )}
                    </div>

                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 leading-tight mb-2">
                      {course.title}
                    </h3>

                    <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-4">
                      {course.description}
                    </p>
                  </div>

                  {/* Card meta */}
                  <div className="px-5 pb-4 mt-auto">
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                      <span className="flex items-center gap-1.5">
                        <CubeIcon className="w-4 h-4 text-slate-400 dark:text-slate-400" />
                        {t('courses.materialsCount', { count: course.modules?.length || 0 })}
                      </span>
                      <span className="flex items-center gap-1.5 text-accent-600 dark:text-accent-300 font-medium">
                        <CheckCircleIcon className="w-4 h-4" />
                        {t('courses.completionsCount', { count: course._count?.trainings || 0 })}
                      </span>
                    </div>

                    {/* Action buttons */}
                    {activeTab === 'templates' ? (
                      <div className="space-y-2">
                        {/* Boundary PSI/SSM: dacă userul nu poate clona acest tip (ex: instructor
                          fără bifa PSI pe un template SU), butonul de clonare e dezactivat și
                          rămâne doar „Vezi detalii" (read-only). Backend-ul aplică același check. */}
                        {canAssignType(user, !!user?.managesDepartment, course.type) ? (
                          <Link to={`/courses/templates/${course.id}`} className="block">
                            <Button
                              variant="primary"
                              size="sm"
                              className="w-full justify-center gap-1.5"
                            >
                              {t('courses.useTemplate')}
                            </Button>
                          </Link>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            className="w-full justify-center gap-1.5"
                            disabled
                            title={t('courses.missingCheckboxTitle', {
                              type: course.type?.endsWith('_SU') ? 'PSI' : 'SSM',
                            })}
                          >
                            {t('courses.viewOnly')}
                          </Button>
                        )}
                        <div className="flex gap-2">
                          <Link to={`/courses/templates/${course.id}`} className="flex-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-center gap-1.5"
                            >
                              {t('courses.viewDetails')}
                            </Button>
                          </Link>
                          {/* Șterge template — doar SUPER_ADMIN pt cele globale, sau SSM_* pt cele proprii */}
                          {(user?.role === 'SUPER_ADMIN' ||
                            (!course.isGlobal &&
                              ['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role))) && (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDelete(course.id)}
                              className="!px-2.5"
                              title={t('courses.deleteTemplateTitle')}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : canManageCourses ? (
                      <div className="flex gap-2">
                        <Link to={`/courses/${course.id}/stats`} className="flex-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-center gap-1.5"
                          >
                            <ChartBarIcon className="w-4 h-4" />
                            {t('courses.stats')}
                          </Button>
                        </Link>
                        <Link to={`/courses/${course.id}/content`} className="flex-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-center gap-1.5"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                            {t('courses.content')}
                          </Button>
                        </Link>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(course.id)}
                          className="!px-2.5"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Link to={`/courses/${course.id}/stats`} className="flex-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-center gap-1.5"
                          >
                            <ChartBarIcon className="w-4 h-4" />
                            {t('courses.stats')}
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={displayedList.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      <ConfirmModal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title={t('courses.deleteTitle')}
        message={t('courses.deleteConfirm')}
        confirmLabel={t('courses.deleteConfirmLabel')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default Courses;
